import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import html2pdf from "html2pdf.js";
import {
  AlertTriangle,
  ArrowUpRight,
  Brain,
  FileDown,
  HelpCircle,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  TextQuote,
} from "lucide-react";

import { getAuthToken } from "../lib/supabase";
import { useUser } from "../lib/UserContext";
import {
  CLINIC_INSIGHTS_WINDOWS,
  buildClinicInsightsConversationHref,
  buildClinicInsightsPdfExportSnapshot,
  buildClinicInsightsSnapshotContext,
  getClinicInsightsChangeTone,
  getClinicInsightsConfidenceBadge,
  getClinicInsightsScoreCardsGridClass,
  getClinicInsightsSentimentBand,
  getClinicInsightsSentimentGaugeArcClipPath,
  getClinicInsightsSentimentNeedleRotation,
  getClinicInsightsScoreDefinition,
  getClinicInsightsStatusTone,
  getClinicInsightsTopicBadge,
  getClinicInsightsTopicSize,
  normalizeClinicInsightsWindow,
} from "../lib/clinicInsights.mjs";

const API_URL = import.meta.env.VITE_API_URL as string;
const PDF_EXPORT_WIDTH_PX = 720;

type ScoreEntry = {
  value: number;
  delta?: number;
};

type CustomerSentiment = {
  score?: number | null;
  label?: string | null;
  summary?: string | null;
  delta?: number | null;
  confidence?: string | null;
  why_this_matters?: string | null;
  change_vs_previous_window?: {
    direction?: string | null;
    delta?: number | null;
    summary?: string | null;
  } | null;
  evidence_highlights?: string[] | null;
};

type ThemeEvidence = {
  utilisateur: string;
  conversation_row_id?: string | null;
  excerpt?: string | null;
  signals?: Record<string, boolean>;
};

type InsightTheme = {
  id?: string;
  rank?: number;
  label: string;
  description?: string | null;
  conversation_count?: number;
  trend?: Record<string, unknown>;
  score_impact?: Record<string, unknown>;
  evidence?: ThemeEvidence[];
  confidence?: string | null;
  why_this_matters?: string | null;
  change_vs_previous_window?: {
    direction?: string | null;
    delta?: number | null;
    summary?: string | null;
  } | null;
  evidence_highlights?: string[] | null;
  affected_sub_scores?: string[] | null;
};

type Recommendation = {
  title: string;
  priority?: string;
  why?: string;
  confidence?: string | null;
  why_this_matters?: string | null;
  linked_signals?: string[] | null;
  change_vs_previous_window?: {
    direction?: string | null;
    delta?: number | null;
    summary?: string | null;
  } | null;
  evidence_highlights?: string[] | null;
};

type AtRiskThread = {
  utilisateur: string;
  profile_name?: string | null;
  risk_score?: number;
  reason?: string;
  last_patient_message?: string | null;
  last_activity_at?: string | null;
  risk_factors?: string[] | null;
  why_this_matters?: string | null;
  evidence_highlights?: string[] | null;
};

type TopicMapEntry = {
  label: string;
  source?: string | null;
  source_label?: string | null;
  conversation_count?: number | null;
  share_percent?: number | null;
  summary?: string | null;
  rank?: number | null;
  evidence?: ThemeEvidence[];
};

type ClinicInsightsSnapshot = {
  id: string;
  status: string;
  window_key: string;
  window_start?: string | null;
  window_end?: string | null;
  generated_at: string;
  summary: string;
  metrics: Record<string, unknown>;
  scores: Record<string, ScoreEntry>;
  customer_sentiment?: CustomerSentiment | null;
  topic_map?: TopicMapEntry[];
  source_conversation_count: number;
  llm_model?: string | null;
  patient_voice_themes: InsightTheme[];
  clinic_response_themes: InsightTheme[];
  recommendations: Recommendation[];
  at_risk_threads: AtRiskThread[];
  comparison_summary?: string | null;
  improving_signals?: string[];
  worsening_signals?: string[];
  stable_signals?: string[];
  analysis_context?: Record<string, unknown>;
  selection_diagnostics?: Record<string, unknown>;
};

type SnapshotContextPayload = {
  metadata: { label: string; value: string }[];
  groups: { label: string; items: string[] }[];
  stats: { label: string; value: number }[];
};

const SCORE_LABELS: Record<string, string> = {
  response_clarity: "Response clarity",
  continuity_of_handling: "Continuity",
  timeliness_perception: "Timeliness",
  operational_friction: "Operational friction",
};

const STATUS_TONE_STYLES: Record<string, string> = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-800",
  warning: "border-amber-200 bg-amber-50 text-amber-800",
  danger: "border-rose-200 bg-rose-50 text-rose-800",
  muted: "border-slate-200 bg-slate-50 text-slate-700",
};

function formatDateTime(value?: string | null) {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-SG", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function tonePillClass(status?: string) {
  const tone = getClinicInsightsStatusTone(status);
  return STATUS_TONE_STYLES[tone] ?? STATUS_TONE_STYLES.muted;
}

function changeToneClass(direction?: string | null) {
  const tone = getClinicInsightsChangeTone(direction ?? "");
  return STATUS_TONE_STYLES[tone] ?? STATUS_TONE_STYLES.muted;
}

function formatConversationCount(count?: number | null) {
  const normalized = Number(count ?? 0);
  return `${normalized} conversation${normalized === 1 ? "" : "s"}`;
}

function formatDateForPdfFilename(value?: string | null) {
  const date = value ? new Date(value) : new Date();
  const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;
  return safeDate.toISOString().slice(0, 10);
}

export default function ClinicInsights() {
  const user = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const windowKey = normalizeClinicInsightsWindow(searchParams.get("window") ?? undefined);
  const exportRef = useRef<HTMLDivElement | null>(null);

  const [snapshot, setSnapshot] = useState<ClinicInsightsSnapshot | null>(null);
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const fetchInsights = useCallback(
    async (mode: "initial" | "refresh" = "initial") => {
      if (!user?.id) return;
      if (mode === "initial") {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);
      try {
        const token = await getAuthToken();
        const params = new URLSearchParams({
          praticien_id: user.id,
          window: windowKey,
        });
        const response = await fetch(
          `${API_URL}/internal/clinic-insights/current?${params.toString()}`,
          token ? { headers: { Authorization: token } } : {},
        );
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(body.detail ?? `Error ${response.status}`);
        }

        setFeatureEnabled(body.feature_enabled !== false);
        setSnapshot(body.snapshot ?? null);
      } catch (fetchError) {
        setError(fetchError instanceof Error ? fetchError.message : "Failed to load Clinic insights");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [user?.id, windowKey],
  );

  useEffect(() => {
    fetchInsights("initial");
  }, [fetchInsights]);

  const setWindow = (nextWindow: string) => {
    const normalized = normalizeClinicInsightsWindow(nextWindow);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("window", normalized);
    setSearchParams(nextParams, { replace: true });
  };
  const selectedWindowLabel =
    CLINIC_INSIGHTS_WINDOWS.find((item) => item.key === windowKey)?.label ?? windowKey;
  const snapshotContext = buildClinicInsightsSnapshotContext(
    snapshot?.analysis_context ?? {},
  ) as SnapshotContextPayload;
  const exportSnapshot = useMemo(
    () => (snapshot ? (buildClinicInsightsPdfExportSnapshot(snapshot) as ClinicInsightsSnapshot) : null),
    [snapshot],
  );
  const exportSnapshotContext = useMemo(
    () =>
      buildClinicInsightsSnapshotContext(
        exportSnapshot?.analysis_context ?? {},
      ) as SnapshotContextPayload,
    [exportSnapshot?.analysis_context],
  );

  const handleExportPdf = useCallback(async () => {
    if (!exportRef.current || !exportSnapshot) return;

    setExportError(null);
    setExporting(true);
    try {
      const filename = `clinic-insights-${windowKey}-${formatDateForPdfFilename(
        exportSnapshot.generated_at,
      )}.pdf`;
      const options = {
        margin: 0.35,
        filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
        },
        jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
      };

      await html2pdf().set(options).from(exportRef.current).save();
    } catch (pdfError) {
      console.error("[clinic-insights-pdf] export failed", pdfError);
      setExportError(
        pdfError instanceof Error ? pdfError.message : "Failed to generate the PDF export",
      );
    } finally {
      setExporting(false);
    }
  }, [exportSnapshot, windowKey]);

  return (
    <div className="space-y-6">
      <section className="relative overflow-hidden rounded-[28px] border border-amber-200/70 bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.26),_transparent_35%),radial-gradient(circle_at_bottom_right,_rgba(45,212,191,0.24),_transparent_32%),linear-gradient(135deg,_#fff7ed_0%,_#f8fafc_58%,_#ecfeff_100%)] px-6 py-7 shadow-sm">
        <div className="absolute right-0 top-0 h-40 w-40 translate-x-10 -translate-y-10 rounded-full bg-cyan-200/30 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-32 w-32 -translate-x-8 translate-y-8 rounded-full bg-amber-200/40 blur-3xl" />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600 backdrop-blur">
              <Sparkles className="h-3.5 w-3.5 text-amber-500" />
              Clinic insights
            </div>
            <div className="space-y-2">
              <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
                Patient experience intelligence for the clinic
              </h1>
              <p className="max-w-2xl text-sm leading-6 text-slate-600">
                A standalone operational view of what patients are experiencing, where the clinic response is
                creating friction, and which conversations need closer attention.
              </p>
            </div>
            {snapshot && (
              <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${tonePillClass(snapshot.status)}`}>
                <Brain className="h-3.5 w-3.5" />
                {snapshot.status === "not_enough_signal"
                  ? "Low signal snapshot"
                  : `Updated ${formatDateTime(snapshot.generated_at)}`}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3 lg:items-end">
            <div className="inline-flex flex-wrap gap-2 rounded-2xl border border-white/70 bg-white/80 p-2 backdrop-blur">
              {CLINIC_INSIGHTS_WINDOWS.map((item) => {
                const active = item.key === windowKey;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setWindow(item.key)}
                    className={
                      active
                        ? "rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm"
                        : "rounded-xl px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
                    }
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => fetchInsights("refresh")}
              disabled={refreshing || loading}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh snapshot
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={!exportSnapshot || exporting || loading}
              className="inline-flex items-center gap-2 rounded-full border border-slate-900 bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-300 disabled:text-white"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              {exporting ? "Generating PDF..." : "Export anonymized PDF"}
            </button>
            {exportError && (
              <p className="max-w-xs text-xs leading-5 text-rose-700">{exportError}</p>
            )}
          </div>
        </div>
      </section>

      {loading ? (
        <div className="flex min-h-[320px] items-center justify-center rounded-[24px] border border-slate-200 bg-white/90">
          <div className="flex items-center gap-3 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm font-medium">Loading Clinic insights…</span>
          </div>
        </div>
      ) : error ? (
        <div className="rounded-[24px] border border-rose-200 bg-rose-50 px-6 py-8 text-rose-800">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="space-y-2">
              <h2 className="text-sm font-semibold">Clinic insights could not be loaded</h2>
              <p className="text-sm text-rose-700">{error}</p>
            </div>
          </div>
        </div>
      ) : !featureEnabled ? (
        <div className="rounded-[24px] border border-slate-200 bg-white px-6 py-8">
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 h-5 w-5 text-slate-500" />
            <div className="space-y-2">
              <h2 className="text-sm font-semibold text-slate-800">Feature not enabled for this clinic</h2>
              <p className="text-sm text-slate-600">
                Enable <span className="font-medium">Clinic insights</span> from the client settings page before using this view.
              </p>
            </div>
          </div>
        </div>
      ) : !snapshot ? (
        <div className="rounded-[24px] border border-slate-200 bg-white px-6 py-10 text-center">
          <div className="mx-auto flex max-w-xl flex-col items-center gap-3">
            <TextQuote className="h-8 w-8 text-slate-300" />
            <h2 className="text-lg font-semibold text-slate-800">No snapshot available yet</h2>
            <p className="text-sm leading-6 text-slate-600">
              The clinic is enabled, but no daily snapshot has been generated for this window yet. Once the scheduled job
              runs, this page will populate with themes, sub-scores and at-risk conversations.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {snapshot.status === "not_enough_signal" && (
            <div className="rounded-[24px] border border-slate-200 bg-slate-50 px-6 py-5">
              <div className="flex items-start gap-3">
                <ShieldAlert className="mt-0.5 h-5 w-5 text-slate-500" />
                <div className="space-y-1">
                  <h2 className="text-sm font-semibold text-slate-800">Not enough signal for deep insight yet</h2>
                  <p className="text-sm text-slate-600">
                    This window only includes {snapshot.source_conversation_count} analyzed conversation(s). The system saved
                    the snapshot, but waits for a denser sample before surfacing themes and recommendations.
                  </p>
                </div>
              </div>
            </div>
          )}

          {snapshot.comparison_summary && (
            <div className="rounded-[24px] border border-slate-200 bg-white px-6 py-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                What changed vs previous window
              </p>
              <p className="mt-3 text-sm leading-6 text-slate-600">{snapshot.comparison_summary}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {(snapshot.improving_signals ?? []).map((signal) => (
                  <span
                    key={`improving-${signal}`}
                    className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700"
                  >
                    Improving: {signal}
                  </span>
                ))}
                {(snapshot.worsening_signals ?? []).map((signal) => (
                  <span
                    key={`worsening-${signal}`}
                    className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-[11px] font-semibold text-rose-700"
                  >
                    Worsening: {signal}
                  </span>
                ))}
              </div>
            </div>
          )}

          <section className="grid items-start gap-6 xl:grid-cols-[0.92fr_1.08fr]">
            <div className="space-y-4">
              <CustomerSentimentPanel
                sentiment={snapshot.customer_sentiment}
                status={snapshot.status}
              />
              <OperationalScoreCardsGrid scores={snapshot.scores} />
            </div>
            <TopicMapPanel
              topics={snapshot.topic_map ?? []}
              status={snapshot.status}
              sourceConversationCount={snapshot.source_conversation_count}
            />
          </section>

          <section className="space-y-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                Operational sub-scores
              </p>
              <h2 className="mt-2 text-xl font-semibold text-slate-900">
                Diagnostic signals behind the overall experience
              </h2>
            </div>
          </section>

          <section className="grid gap-6 xl:grid-cols-2">
            <ThemeColumn
              title="Patient voice"
              subtitle="What patients are signaling across conversations"
              accentClass="from-amber-100 via-white to-orange-50"
              themes={snapshot.patient_voice_themes}
            />
            <ThemeColumn
              title="Clinic response"
              subtitle="Where the clinic workflow shapes perceived experience"
              accentClass="from-cyan-100 via-white to-sky-50"
              themes={snapshot.clinic_response_themes}
            />
          </section>

          <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
            <div className="space-y-6">
              <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Summary</p>
                <h2 className="mt-3 text-xl font-semibold text-slate-900">Operational readout for this window</h2>
                <p className="mt-3 text-sm leading-7 text-slate-600">
                  {snapshot.summary || "No narrative summary was generated for this snapshot."}
                </p>
                {(snapshotContext.metadata.length > 0 ||
                  snapshotContext.groups.length > 0 ||
                  snapshotContext.stats.length > 0) && (
                  <div className="mt-4 rounded-[20px] border border-slate-200 bg-[linear-gradient(180deg,_#f8fafc_0%,_#eef2ff_100%)] px-4 py-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
                      Snapshot context
                    </p>
                    {snapshotContext.metadata.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {snapshotContext.metadata.map((item) => (
                          <span
                            key={`${item.label}-${item.value}`}
                            className="inline-flex items-center gap-2 rounded-full border border-white/90 bg-white px-3 py-1.5 text-xs text-slate-600 shadow-sm"
                          >
                            <span className="font-semibold uppercase tracking-[0.08em] text-slate-400">
                              {item.label}
                            </span>
                            <span className="font-medium text-slate-700">{item.value}</span>
                          </span>
                        ))}
                      </div>
                    )}
                    {snapshotContext.groups.length > 0 && (
                      <div className="mt-4 space-y-3">
                        {snapshotContext.groups.map((group) => (
                          <div key={group.label}>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                              {group.label}
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {group.items.map((item) => (
                                <span
                                  key={`${group.label}-${item}`}
                                  className="rounded-full border border-slate-200 bg-white/90 px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm"
                                >
                                  {item}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {snapshotContext.stats.length > 0 && (
                      <div className="mt-4">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                          Workflow markers
                        </p>
                        <div className="mt-2 grid gap-2 sm:grid-cols-3">
                          {snapshotContext.stats.map((stat) => (
                            <div
                              key={stat.label}
                              className="rounded-2xl border border-white/90 bg-white px-3 py-3 shadow-sm"
                            >
                              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">
                                {stat.label}
                              </p>
                              <p className="mt-1 text-lg font-semibold text-slate-900">{stat.value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {false && false && snapshot.analysis_context && Object.keys(snapshot.analysis_context).length > 0 && (
                  <div className="mt-4 rounded-[20px] border border-slate-200 bg-[linear-gradient(180deg,_#f8fafc_0%,_#eef2ff_100%)] px-4 py-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
                      Snapshot context
                    </p>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      {Object.entries(snapshot.analysis_context)
                        .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : String(value)}`)
                        .join(" · ")}
                    </p>
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-500">
                  <span className="rounded-full bg-slate-100 px-3 py-1">
                    {snapshot.source_conversation_count} conversations analyzed
                  </span>
                  {snapshot.llm_model && (
                    <span className="rounded-full bg-slate-100 px-3 py-1">
                      Model: {snapshot.llm_model}
                    </span>
                  )}
                </div>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Recommendations
                    </p>
                    <h2 className="mt-2 text-xl font-semibold text-slate-900">Actions worth taking next</h2>
                  </div>
                </div>
                <div className="mt-5 space-y-3">
                  {snapshot.recommendations.length > 0 ? (
                    snapshot.recommendations.map((item) => (
                      <div key={item.title} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h3 className="text-sm font-semibold text-slate-900">{item.title}</h3>
                            {(item.why || item.why_this_matters) && (
                              <p className="mt-1 text-sm leading-6 text-slate-600">
                                {item.why || item.why_this_matters}
                              </p>
                            )}
                            {item.linked_signals && item.linked_signals.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {item.linked_signals.map((signal) => (
                                  <span
                                    key={`${item.title}-${signal}`}
                                    className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600"
                                  >
                                    {signal}
                                  </span>
                                ))}
                              </div>
                            )}
                            {item.evidence_highlights && item.evidence_highlights.length > 0 && (
                              <p className="mt-3 text-xs leading-5 text-slate-500">
                                Evidence: {item.evidence_highlights.join(" · ")}
                              </p>
                            )}
                          </div>
                          {item.priority && (
                            <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                              {item.priority}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No operational recommendation was generated for this window.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">At-risk conversations</p>
              <h2 className="mt-2 text-xl font-semibold text-slate-900">Threads to review more closely</h2>
              <div className="mt-5 space-y-3">
                {snapshot.at_risk_threads.length > 0 ? (
                  snapshot.at_risk_threads.map((thread) => (
                    <Link
                      key={`${thread.utilisateur}-${thread.last_activity_at ?? "none"}`}
                      to={buildClinicInsightsConversationHref(thread.utilisateur)}
                      className="group block rounded-2xl border border-slate-200 bg-slate-50/70 p-4 transition hover:border-slate-300 hover:bg-white"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-slate-900">
                              {thread.profile_name || thread.utilisateur}
                            </span>
                            {typeof thread.risk_score === "number" && (
                              <span className="rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700">
                                Risk {thread.risk_score}
                              </span>
                            )}
                          </div>
                          <p className="text-sm leading-6 text-slate-600">{thread.reason || "Operational review recommended."}</p>
                          {thread.last_patient_message && (
                            <p className="text-xs leading-5 text-slate-500">
                              Last patient message: “{thread.last_patient_message}”
                            </p>
                          )}
                          {thread.why_this_matters && (
                            <p className="text-xs leading-5 text-slate-500">{thread.why_this_matters}</p>
                          )}
                          {thread.risk_factors && thread.risk_factors.length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-1">
                              {thread.risk_factors.map((factor) => (
                                <span
                                  key={`${thread.utilisateur}-${factor}`}
                                  className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600"
                                >
                                  {factor}
                                </span>
                              ))}
                            </div>
                          )}
                          <p className="text-xs text-slate-400">Last activity: {formatDateTime(thread.last_activity_at)}</p>
                        </div>
                        <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-slate-400 transition group-hover:text-slate-700" />
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">No conversation currently stands out as operationally at risk.</p>
                )}
              </div>
            </div>
          </section>
        </div>
      )}
      {exportSnapshot && (
        <div
          aria-hidden="true"
          style={{
            background: "#ffffff",
            left: "-10000px",
            overflow: "visible",
            pointerEvents: "none",
            position: "fixed",
            top: 0,
            width: `${PDF_EXPORT_WIDTH_PX}px`,
          }}
        >
          <div ref={exportRef}>
            <ClinicInsightsPdfExtract
              snapshot={exportSnapshot}
              snapshotContext={exportSnapshotContext}
              windowLabel={selectedWindowLabel}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ClinicInsightsPdfExtract({
  snapshot,
  snapshotContext,
  windowLabel,
}: {
  snapshot: ClinicInsightsSnapshot;
  snapshotContext: SnapshotContextPayload;
  windowLabel: string;
}) {
  const scoreEntries = Object.entries(snapshot.scores ?? {}) as [string, ScoreEntry][];
  const sentiment = snapshot.customer_sentiment;
  const sentimentScore =
    typeof sentiment?.score === "number" && Number.isFinite(sentiment.score) ? sentiment.score : null;
  const sentimentLabel = (sentiment?.label || getClinicInsightsSentimentBand(sentimentScore)).trim();
  const recommendations = snapshot.recommendations ?? [];
  const atRiskThreads = snapshot.at_risk_threads ?? [];

  return (
    <article
      className="bg-white px-8 py-8 text-slate-900"
      style={{
        boxSizing: "border-box",
        fontFamily: "Arial, sans-serif",
        width: `${PDF_EXPORT_WIDTH_PX}px`,
      }}
    >
      <header className="border-b border-slate-200 pb-5">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          Anonymized PDF extract
        </p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">
          Clinic insights
        </h1>
        <div className="mt-3 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
          <p>Window: {windowLabel}</p>
          <p>Generated: {formatDateTime(snapshot.generated_at)}</p>
          <p>Status: {snapshot.status}</p>
          <p>{formatConversationCount(snapshot.source_conversation_count)} analyzed</p>
        </div>
        <p className="mt-4 rounded-2xl border border-amber-100 bg-gradient-to-r from-amber-50 via-white to-cyan-50 px-4 py-3 text-xs leading-5 text-slate-600">
          Patient display names, WhatsApp identifiers, phone numbers and emails are anonymized in this extract.
        </p>
      </header>

      <PdfSection
        eyebrow="Executive summary"
        title="Operational readout"
        surfaceClass="border-amber-100 bg-gradient-to-br from-amber-50 via-white to-cyan-50"
      >
        <p className="text-sm leading-6 text-slate-700">
          {snapshot.summary || "No narrative summary was generated for this snapshot."}
        </p>
        {snapshot.comparison_summary && (
          <p className="mt-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-700">
            <span className="font-semibold text-slate-900">Change vs previous window: </span>
            {snapshot.comparison_summary}
          </p>
        )}
      </PdfSection>

      <PdfSection
        eyebrow="Patient experience"
        title="Sentiment and diagnostic scores"
        surfaceClass="border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-cyan-50"
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl border border-indigo-100 bg-white/85 px-4 py-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
              Customer sentiment
            </p>
            <p className="mt-2 text-3xl font-semibold text-slate-950">
              {sentimentScore !== null ? sentimentScore.toFixed(1) : "N/A"}
            </p>
            <p className="mt-1 text-sm font-semibold text-slate-700">{sentimentLabel}</p>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              {sentiment?.summary || "Sentiment is unavailable for this window."}
            </p>
          </div>
          <div className="grid gap-3">
            {scoreEntries.map(([key, value]) => (
              <div key={key} className="rounded-2xl border border-white/80 bg-white/85 px-4 py-3 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                    {SCORE_LABELS[key] ?? key}
                  </p>
                  <p className="text-lg font-semibold text-slate-950">{value.value}</p>
                </div>
                <p className="mt-1 text-xs text-slate-500">Delta: {value.delta ?? 0}</p>
              </div>
            ))}
          </div>
        </div>
      </PdfSection>

      <PdfSection
        eyebrow="Topics"
        title="Top patient topics"
        surfaceClass="border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-blue-50"
      >
        {(snapshot.topic_map ?? []).length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {(snapshot.topic_map ?? []).map((topic, index) => {
              const isOtherTopic =
                String(topic.label || "").trim().toLowerCase() === "other / mixed topics";
              const surfaceClass = isOtherTopic
                ? "bg-gradient-to-br from-slate-500 to-slate-700"
                : [
                    "bg-gradient-to-br from-blue-600 to-blue-800",
                    "bg-gradient-to-br from-sky-500 to-blue-700",
                    "bg-gradient-to-br from-indigo-500 to-indigo-700",
                    "bg-gradient-to-br from-emerald-500 to-blue-700",
                  ][index % 4];

              return (
                <div
                  key={`${topic.label}-${topic.rank ?? index}`}
                  className={`${surfaceClass} rounded-2xl px-4 py-3 text-white shadow-sm`}
                  style={{ pageBreakInside: "avoid" }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-semibold text-white">{topic.label}</p>
                    <span className="rounded-full bg-white/20 px-2.5 py-1 text-xs font-semibold text-white">
                      {Number(topic.share_percent ?? 0)}%
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-white/85">
                    {formatConversationCount(topic.conversation_count)}
                  </p>
                  {topic.summary && <p className="mt-2 text-sm leading-6 text-white/90">{topic.summary}</p>}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No dominant topic detected for this window.</p>
        )}
      </PdfSection>

      <div className="grid gap-4 sm:grid-cols-2">
        <PdfThemes title="Patient voice" themes={snapshot.patient_voice_themes ?? []} />
        <PdfThemes title="Clinic response" themes={snapshot.clinic_response_themes ?? []} />
      </div>

      <PdfSection
        eyebrow="Recommendations"
        title="Actions worth taking next"
        surfaceClass="border-orange-100 bg-gradient-to-br from-orange-50 via-white to-amber-50"
      >
        {recommendations.length > 0 ? (
          <div className="space-y-3">
            {recommendations.map((item) => (
              <div
                key={item.title}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3"
                style={{ pageBreakInside: "avoid" }}
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-sm font-semibold text-slate-950">{item.title}</h3>
                  {item.priority && (
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold uppercase text-slate-700">
                      {item.priority}
                    </span>
                  )}
                </div>
                {(item.why || item.why_this_matters) && (
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {item.why || item.why_this_matters}
                  </p>
                )}
                {item.linked_signals && item.linked_signals.length > 0 && (
                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    Signals: {item.linked_signals.join(", ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No operational recommendation was generated for this window.</p>
        )}
      </PdfSection>

      <PdfSection
        eyebrow="At-risk conversations"
        title="Threads to review"
        surfaceClass="border-rose-100 bg-gradient-to-br from-rose-50 via-white to-orange-50"
      >
        {atRiskThreads.length > 0 ? (
          <div className="space-y-3">
            {atRiskThreads.map((thread) => (
              <div
                key={`${thread.utilisateur}-${thread.last_activity_at ?? "none"}`}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3"
                style={{ pageBreakInside: "avoid" }}
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-sm font-semibold text-slate-950">
                    {thread.profile_name || thread.utilisateur}
                  </h3>
                  {typeof thread.risk_score === "number" && (
                    <span className="rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
                      Risk {thread.risk_score}
                    </span>
                  )}
                </div>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {thread.reason || "Operational review recommended."}
                </p>
                {thread.last_patient_message && (
                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    Last patient message: "{thread.last_patient_message}"
                  </p>
                )}
                {thread.risk_factors && thread.risk_factors.length > 0 && (
                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    Risk factors: {thread.risk_factors.join(", ")}
                  </p>
                )}
                <p className="mt-2 text-xs text-slate-400">
                  Last activity: {formatDateTime(thread.last_activity_at)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No conversation currently stands out as operationally at risk.</p>
        )}
      </PdfSection>

      {(snapshotContext.metadata.length > 0 ||
        snapshotContext.groups.length > 0 ||
        snapshotContext.stats.length > 0) && (
        <PdfSection
          eyebrow="Snapshot context"
          title="Analysis context"
          surfaceClass="border-sky-100 bg-gradient-to-br from-sky-50 via-white to-indigo-50"
        >
          {snapshotContext.metadata.length > 0 && (
            <p className="text-sm leading-6 text-slate-600">
              {snapshotContext.metadata.map((item) => `${item.label}: ${item.value}`).join(" | ")}
            </p>
          )}
          {snapshotContext.groups.map((group) => (
            <p key={group.label} className="mt-2 text-sm leading-6 text-slate-600">
              <span className="font-semibold text-slate-900">{group.label}: </span>
              {group.items.join(", ")}
            </p>
          ))}
          {snapshotContext.stats.length > 0 && (
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {snapshotContext.stats.map((stat) => `${stat.label}: ${stat.value}`).join(" | ")}
            </p>
          )}
        </PdfSection>
      )}
    </article>
  );
}

function PdfSection({
  children,
  eyebrow,
  surfaceClass = "border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-100",
  title,
}: {
  children: ReactNode;
  eyebrow: string;
  surfaceClass?: string;
  title: string;
}) {
  return (
    <section className="mt-6" style={{ pageBreakInside: "avoid" }}>
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{eyebrow}</p>
      <h2 className="mt-1 text-lg font-semibold text-slate-950">{title}</h2>
      <div className={`mt-3 rounded-3xl border p-4 ${surfaceClass}`}>{children}</div>
    </section>
  );
}

function PdfThemes({
  themes,
  title,
}: {
  themes: InsightTheme[];
  title: string;
}) {
  const surfaceClass =
    title === "Patient voice"
      ? "border-amber-100 bg-gradient-to-br from-amber-50 via-white to-orange-50"
      : "border-cyan-100 bg-gradient-to-br from-cyan-50 via-white to-sky-50";

  return (
    <PdfSection eyebrow="Themes" title={title} surfaceClass={surfaceClass}>
      {themes.length > 0 ? (
        <div className="space-y-3">
          {themes.map((theme) => (
            <div
              key={`${theme.label}-${theme.rank ?? 0}`}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3"
              style={{ pageBreakInside: "avoid" }}
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-sm font-semibold text-slate-950">{theme.label}</h3>
                {theme.rank && (
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                    #{theme.rank}
                  </span>
                )}
              </div>
              {typeof theme.conversation_count === "number" && (
                <p className="mt-1 text-xs text-slate-500">
                  {formatConversationCount(theme.conversation_count)}
                </p>
              )}
              {theme.description && <p className="mt-2 text-sm leading-6 text-slate-600">{theme.description}</p>}
              {theme.why_this_matters && (
                <p className="mt-2 text-xs leading-5 text-slate-500">{theme.why_this_matters}</p>
              )}
              {theme.evidence_highlights && theme.evidence_highlights.length > 0 && (
                <p className="mt-2 text-xs leading-5 text-slate-500">
                  Evidence: {theme.evidence_highlights.join(" | ")}
                </p>
              )}
              {theme.evidence && theme.evidence.length > 0 && (
                <p className="mt-2 text-xs leading-5 text-slate-500">
                  Examples: {theme.evidence.map((item) => item.excerpt || item.utilisateur).join(" | ")}
                </p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500">No theme detected yet for this lens in the selected window.</p>
      )}
    </PdfSection>
  );
}

function OperationalScoreCardsGrid({
  scores,
}: {
  scores: Record<string, ScoreEntry>;
}) {
  return (
    <div className={getClinicInsightsScoreCardsGridClass()}>
      {Object.entries(scores).map(([key, value]) => {
        const scoreLabel = SCORE_LABELS[key] ?? key;
        const scoreDefinition = getClinicInsightsScoreDefinition(key);
        const tooltipId = `clinic-insights-score-${key}`;

        return (
          <div key={key} className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{scoreLabel}</p>
              {scoreDefinition && (
                <div className="group/tooltip relative shrink-0">
                  <button
                    type="button"
                    aria-label={`${scoreLabel}: ${scoreDefinition}`}
                    aria-describedby={tooltipId}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
                  >
                    <HelpCircle className="h-3.5 w-3.5" />
                  </button>
                  <div
                    id={tooltipId}
                    role="tooltip"
                    className="pointer-events-none absolute right-0 top-full z-10 mt-2 w-64 translate-y-1 rounded-2xl border border-slate-200 bg-slate-950 px-3 py-2 text-left text-xs leading-5 text-slate-100 opacity-0 shadow-xl transition duration-150 group-hover/tooltip:translate-y-0 group-hover/tooltip:opacity-100 group-focus-within/tooltip:translate-y-0 group-focus-within/tooltip:opacity-100"
                  >
                    {scoreDefinition}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-3 flex items-end justify-between gap-3">
              <div>
                <div className="text-[2rem] font-semibold leading-none text-slate-900">{value.value}</div>
                <p className="mt-2 text-xs text-slate-500">out of 100</p>
              </div>
              <div
                className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                  (value.delta ?? 0) < 0
                    ? "bg-rose-50 text-rose-700"
                    : (value.delta ?? 0) > 0
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-slate-100 text-slate-600"
                }`}
              >
                {(value.delta ?? 0) > 0 ? `+${value.delta}` : value.delta ?? 0}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const TOPIC_SIZE_STYLES: Record<string, string> = {
  large: "min-h-[158px]",
  medium: "min-h-[132px]",
  small: "min-h-[118px]",
};

const TOPIC_SURFACE_STYLES = [
  "bg-[linear-gradient(180deg,_#2563eb_0%,_#1d4ed8_100%)]",
  "bg-[linear-gradient(180deg,_#3b82f6_0%,_#2563eb_100%)]",
  "bg-[linear-gradient(180deg,_#6366f1_0%,_#4f46e5_100%)]",
  "bg-[linear-gradient(180deg,_#0ea5e9_0%,_#2563eb_100%)]",
];

function CustomerSentimentPanel({
  sentiment,
  status,
}: {
  sentiment?: CustomerSentiment | null;
  status?: string;
}) {
  const numericScore =
    typeof sentiment?.score === "number" && Number.isFinite(sentiment.score)
      ? sentiment.score
      : null;
  const label = (sentiment?.label || getClinicInsightsSentimentBand(numericScore)).trim();
  const summary =
    numericScore !== null
      ? (sentiment?.summary || "Patients are receiving a mixed but usable experience in this window.").trim()
      : status === "not_enough_signal"
        ? "This window does not yet include enough analyzed conversations to assess overall patient sentiment."
        : "Sentiment is unavailable for this window.";
  const delta =
    typeof sentiment?.delta === "number" && Number.isFinite(sentiment.delta) ? sentiment.delta : null;
  const confidenceLabel = getClinicInsightsConfidenceBadge(sentiment?.confidence ?? "");
  const changeSummary = sentiment?.change_vs_previous_window?.summary?.trim();
  const evidenceHighlights = sentiment?.evidence_highlights ?? [];
  const needleRotation = getClinicInsightsSentimentNeedleRotation(numericScore);
  const arcClipPath = getClinicInsightsSentimentGaugeArcClipPath();

  return (
    <div className="relative overflow-hidden rounded-[28px] border border-indigo-200/70 bg-[radial-gradient(circle_at_top_left,_rgba(99,102,241,0.22),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(34,211,238,0.18),_transparent_36%),linear-gradient(145deg,_#eff6ff_0%,_#f8fafc_52%,_#eef2ff_100%)] p-6 shadow-sm">
      <div className="absolute right-0 top-0 h-32 w-32 translate-x-8 -translate-y-8 rounded-full bg-indigo-300/30 blur-3xl" />
      <div className="absolute bottom-0 left-0 h-28 w-28 -translate-x-6 translate-y-6 rounded-full bg-cyan-200/40 blur-3xl" />
      <div className="relative">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/80 bg-indigo-600 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white shadow-sm">
          <Sparkles className="h-3.5 w-3.5" />
          Customer sentiment score
        </div>
        <div className="mt-5 rounded-[24px] border border-white/80 bg-white/88 p-5 backdrop-blur">
          {numericScore !== null ? (
            <div className="space-y-5">
              <div className="flex flex-col items-center">
                <div className="relative h-[164px] w-[280px]">
                  <div
                    className="absolute left-1/2 top-0 h-[280px] w-[280px] -translate-x-1/2 rounded-full bg-[linear-gradient(135deg,_#d9f99d_0%,_#a7f3d0_34%,_#67e8f9_100%)] shadow-[inset_0_8px_30px_rgba(15,23,42,0.06)]"
                    style={{ clipPath: arcClipPath }}
                  />
                  <div
                    className="absolute left-1/2 top-3 h-[244px] w-[244px] -translate-x-1/2 rounded-full border border-white/20"
                    style={{ clipPath: arcClipPath }}
                  />
                  <div
                    className="absolute left-1/2 top-0 h-[280px] w-[280px] -translate-x-1/2 rounded-full border border-emerald-200/70"
                    style={{ clipPath: arcClipPath }}
                  />
                  <div
                    className="absolute bottom-[22px] left-1/2 h-[92px] w-1.5 -translate-x-1/2 rounded-full bg-slate-900 shadow-sm"
                    style={{
                      transform: `translateX(-50%) rotate(${needleRotation}deg)`,
                      transformOrigin: "bottom center",
                    }}
                  />
                  <div className="absolute bottom-[16px] left-1/2 h-4 w-4 -translate-x-1/2 rounded-full border border-slate-900 bg-white shadow-sm" />
                  <div className="absolute inset-x-[78px] bottom-0 h-7 rounded-t-full bg-white shadow-[0_-4px_20px_rgba(255,255,255,0.95)]" />
                </div>
                <div className="mt-2 text-center">
                  <div className="text-6xl font-semibold leading-none text-slate-900">
                    {numericScore.toFixed(1)}
                  </div>
                  <p className="mt-3 text-sm text-slate-500">out of 5.0</p>
                  <p className="mt-3 text-lg font-semibold text-amber-600">{label}</p>
                </div>
              </div>
              <div className="space-y-3">
                {confidenceLabel && (
                  <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                    {confidenceLabel}
                  </span>
                )}
                <p className="text-sm leading-6 text-slate-600">{summary}</p>
                {sentiment?.why_this_matters && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Why this matters</p>
                    <p className="mt-2 text-sm leading-6 text-slate-600">{sentiment.why_this_matters}</p>
                  </div>
                )}
                {changeSummary && (
                  <p className="text-xs leading-5 text-slate-500">{changeSummary}</p>
                )}
                {delta !== null && delta !== 0 && (
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                      delta > 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
                    }`}
                  >
                    {delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1)} vs previous equivalent window
                  </span>
                )}
                {evidenceHighlights.length > 0 && (
                  <p className="text-xs leading-5 text-slate-500">
                    Evidence: {evidenceHighlights.join(" · ")}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-4 py-5">
              <p className="text-sm font-semibold text-slate-800">Sentiment unavailable for this window</p>
              <p className="mt-2 text-sm leading-6 text-slate-600">{summary}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TopicMapPanel({
  topics,
  status,
  sourceConversationCount,
}: {
  topics: TopicMapEntry[];
  status?: string;
  sourceConversationCount: number;
}) {
  const hasTopics = topics.length > 0;
  const hasOtherBucket = topics.some(
    (topic) => String(topic.label || "").trim().toLowerCase() === "other / mixed topics",
  );
  const totalTopicConversations = topics.reduce(
    (sum, topic) => sum + Number(topic.conversation_count ?? 0),
    0,
  );
  const coveragePercent =
    sourceConversationCount > 0
      ? Math.min(100, Math.round((totalTopicConversations * 100) / sourceConversationCount))
      : 0;
  const emptyMessage =
    status === "not_enough_signal"
      ? "Topics need more signal before the page can surface a reliable category map for this window."
      : "No dominant topic detected for this window.";

  return (
    <div className="relative overflow-hidden rounded-[28px] border border-emerald-200/70 bg-[radial-gradient(circle_at_top_left,_rgba(34,197,94,0.22),_transparent_28%),radial-gradient(circle_at_bottom_right,_rgba(37,99,235,0.18),_transparent_34%),linear-gradient(145deg,_#ecfeff_0%,_#f8fafc_46%,_#eff6ff_100%)] p-6 shadow-sm">
      <div className="absolute left-0 top-0 h-36 w-36 -translate-x-8 -translate-y-8 rounded-full bg-emerald-300/25 blur-3xl" />
      <div className="absolute bottom-0 right-0 h-32 w-32 translate-x-8 translate-y-8 rounded-full bg-blue-200/35 blur-3xl" />
      <div className="relative">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/80 bg-emerald-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white shadow-sm">
          <Brain className="h-3.5 w-3.5" />
          Topics
        </div>
        <div className="mt-5 rounded-[24px] border border-white/80 bg-white/88 p-5 backdrop-blur">
          <div className="mb-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                Top patient topics
              </p>
              {hasTopics && (
                <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600">
                  {coveragePercent}% of conversations covered
                </span>
              )}
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              The panel shows the top patient topics until it covers at least 80% of analyzed conversations, then
              groups the remainder into <span className="font-medium">Other / mixed topics</span>.
            </p>
            {hasOtherBucket && (
              <p className="mt-2 text-xs leading-5 text-slate-500">
                Lower-volume or mixed conversations are rolled up into the final bucket so the visible cards cover the
                full scope.
              </p>
            )}
          </div>
          {hasTopics ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {topics.map((topic, index) => {
                const size = getClinicInsightsTopicSize(topic);
                const isOtherTopic =
                  String(topic.label || "").trim().toLowerCase() === "other / mixed topics";
                const surfaceClass = isOtherTopic
                  ? "bg-[linear-gradient(180deg,_#94a3b8_0%,_#64748b_100%)]"
                  : TOPIC_SURFACE_STYLES[index % TOPIC_SURFACE_STYLES.length];
                const badgeLabel = topic.source_label || getClinicInsightsTopicBadge(topic.source);
                const isFeatured = index === 0 && topics.length > 2 && !isOtherTopic;

                return (
                  <div
                    key={`${topic.label}-${topic.rank ?? index}`}
                    className={`${TOPIC_SIZE_STYLES[size] ?? TOPIC_SIZE_STYLES.small} ${
                      isFeatured ? "sm:col-span-2" : ""
                    } ${surfaceClass} overflow-hidden rounded-[22px] p-4 text-white shadow-sm`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className="inline-flex rounded-full bg-white/18 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/95">
                        {index === 0 && !isOtherTopic ? `Top topic · ${badgeLabel}` : badgeLabel}
                      </span>
                      <span className="text-[11px] font-medium text-white/85">
                        {Number(topic.share_percent ?? 0)}%
                      </span>
                    </div>
                    <div>
                      <p className="text-base font-semibold leading-5 text-white">{topic.label}</p>
                      <p className="mt-2 text-xs text-white/85">{formatConversationCount(topic.conversation_count)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-4 py-5">
              <p className="text-sm font-semibold text-slate-800">Topics unavailable for this window</p>
              <p className="mt-2 text-sm leading-6 text-slate-600">{emptyMessage}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ThemeColumn({
  title,
  subtitle,
  accentClass,
  themes,
}: {
  title: string;
  subtitle: string;
  accentClass: string;
  themes: InsightTheme[];
}) {
  return (
    <div className={`rounded-[24px] border border-slate-200 bg-gradient-to-br ${accentClass} p-6 shadow-sm`}>
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{title}</p>
        <h2 className="mt-2 text-xl font-semibold text-slate-900">{subtitle}</h2>
      </div>
      <div className="mt-5 space-y-4">
        {themes.length > 0 ? (
          themes.map((theme) => (
            <div key={`${theme.label}-${theme.rank ?? 0}`} className="rounded-2xl border border-white/80 bg-white/85 p-4 backdrop-blur">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{theme.label}</p>
                  {typeof theme.conversation_count === "number" && (
                    <p className="mt-1 text-xs text-slate-500">
                      {theme.conversation_count} conversation{theme.conversation_count > 1 ? "s" : ""}
                    </p>
                  )}
                  {theme.why_this_matters && (
                    <p className="mt-2 text-xs leading-5 text-slate-500">{theme.why_this_matters}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  {theme.rank && (
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                      #{theme.rank}
                    </span>
                  )}
                  {getClinicInsightsConfidenceBadge(theme.confidence ?? "") && (
                    <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                      {getClinicInsightsConfidenceBadge(theme.confidence ?? "")}
                    </span>
                  )}
                </div>
              </div>
              {theme.description && <p className="mt-3 text-sm leading-6 text-slate-600">{theme.description}</p>}
              {theme.change_vs_previous_window?.summary && (
                <p className={`mt-3 inline-flex rounded-full border px-3 py-1 text-[11px] font-semibold ${changeToneClass(theme.change_vs_previous_window.direction)}`}>
                  {theme.change_vs_previous_window.summary}
                </p>
              )}
              {theme.evidence_highlights && theme.evidence_highlights.length > 0 && (
                <p className="mt-3 text-xs leading-5 text-slate-500">
                  Evidence: {theme.evidence_highlights.join(" · ")}
                </p>
              )}
              {theme.evidence && theme.evidence.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {theme.evidence.map((item) => (
                    <span
                      key={`${item.utilisateur}-${item.conversation_row_id ?? item.excerpt ?? "evidence"}`}
                      className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600"
                    >
                      {item.excerpt || item.utilisateur}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))
        ) : (
          <p className="rounded-2xl border border-dashed border-slate-200 bg-white/70 px-4 py-6 text-sm text-slate-500">
            No theme detected yet for this lens in the selected window.
          </p>
        )}
      </div>
    </div>
  );
}
